/**
 * Fetches and parses the auto-generated OpenAPI document at /api/docs-json
 * (NestJS Swagger, already wired in main.ts) into a shape the docs page can
 * render directly. This is intentionally NOT hand-maintained data — every
 * field here traces back to the real @ApiOperation/@ApiProperty/@ApiResponse
 * decorators added to the mobile controllers, so it stays in sync with the
 * actual backend automatically.
 *
 * /api/docs-json is bare and unauthenticated (same exclusion as /api/docs
 * itself) — a plain fetch() is used rather than the tenant-scoped axios
 * instance in lib/axios.ts, since no X-School-Code/Bearer header is needed
 * or wanted here.
 *
 * SCOPE: /api/docs-json reflects the ENTIRE backend's Swagger-tagged
 * routes — the `mobile-legacy` tag alone still includes routes this page
 * doesn't track yet, alongside the 90-endpoint Student/Parent/General
 * real app list this page originally existed to document, PLUS (as of
 * this comment) a growing Staff/Teacher App surface — see
 * docs/TEACHER-APP-DETAILED-REFERENCE.md and the "Staff & Teacher" group
 * below. This page filters down to exactly the paths in REAL_APP_ENDPOINTS
 * that currently have a real, working route (originally 81 of the
 * 90-endpoint Student/Parent/General list — 72 live-verified + 9 payment
 * endpoints built and fixture-tested, paused on Razorpay credentials; the
 * other 9 of that 90-path list are BLOCKED, no route exists on purpose,
 * see docs/MOBILE-APP-REAL-ENDPOINTS.md — plus each Staff/Teacher endpoint
 * as it's built). /api/docs-json's own response is untouched; filtering
 * happens client-side, here, for this page's display only.
 */

export interface RequestField {
  name: string;
  type: string;
  required: boolean;
  description: string;
  /** Per-property `@ApiProperty({ example })` value, if the decorator set one — used to pre-fill the Try Live form. */
  example?: unknown;
}

export interface NamedExample {
  status: string;
  description: string;
  example: unknown;
}

export interface MobileApiEndpoint {
  method: string;
  path: string;
  summary: string;
  requiresAuth: boolean;
  requestFields: RequestField[];
  successResponse: NamedExample | null;
  errorResponses: NamedExample[];
  /** True when this endpoint's shape was DESIGNED for this mobile API layer, not matched against a frozen mobile-app contract's saved example. Two real conventions exist across the backend's decorators (see isDesignedNote) — both are honored. */
  isDesigned: boolean;
}

const HTTP_METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const;

/**
 * The exact 183 (method, path) pairs currently marked BUILT (live-verified
 * or fixture-tested-only) across ALL known sources — see
 * docs/MOBILE-API-STATUS-AUDIT.md's consolidated table (214 distinct
 * endpoints total — unchanged by the 2026-08-21 Leave Management batch,
 * since those 5 rows were already tracked as NOT YET BUILT, not new; 2
 * blocked, 3 confirmed dead, 23 not yet built). Leave Management is the
 * LAST remaining tracked domain — every other row is BUILT/BLOCKED/DEAD.
 * Exact-match only, both method AND path — never a prefix/substring
 * match, so a similar-looking staff/teacher/web-portal route can never
 * leak in by accident. The 9 Razorpay payment endpoints are included as
 * ordinary entries (paused on credentials, not blocked). `/api/teacher/
 * login` is a route-level alias of `/api/staff/login` (same controller/
 * method) — see StaffLoginController's header comment. Keep this in
 * lockstep with the audit doc's tally when a new batch ships or a blocked
 * endpoint gets built.
 */
const REAL_APP_ENDPOINTS: ReadonlyArray<{ method: string; path: string }> = [
  // General (9)
  { method: 'post', path: '/api/logout' },
  { method: 'get', path: '/api/holidays' },
  { method: 'post', path: '/api/change-password' },
  { method: 'get', path: '/api/gallery' },
  { method: 'get', path: '/api/session-years' },
  { method: 'post', path: '/api/get-related-student-guardian' },
  { method: 'post', path: '/api/authenticate-without-credentials' },
  { method: 'post', path: '/api/update-profile' },
  { method: 'post', path: '/api/fees/create-payment' },
  { method: 'post', path: '/api/fees/verify-payment' },
  // Staff & Teacher (60 — see docs/MOBILE-API-STATUS-AUDIT.md; ongoing build,
  // remaining gaps tracked in docs/TEACHER-APP-DETAILED-REFERENCE.md)
  { method: 'post', path: '/api/forgot-password' },
  { method: 'get', path: '/api/settings' },
  { method: 'get', path: '/api/staff/counter' },
  { method: 'get', path: '/api/staff/users' },
  { method: 'get', path: '/api/classes' },
  { method: 'get', path: '/api/medium' },
  { method: 'post', path: '/api/staff/login' },
  // Route-level alias of /api/staff/login — same StaffLoginController
  // method, same frozen-contract response (see that controller's header
  // comment and docs/TEACHER-APP-REAL-CONTROLLER-INVESTIGATION.md: the
  // real legacy TeacherApiController::login() is registered under both
  // /teacher/login and /staff/login).
  { method: 'post', path: '/api/teacher/login' },
  { method: 'post', path: '/api/staff/reset-password' },
  // Leave Management — real legacy `leaves`/`leave_details`/
  // `leave_masters` (71/74/246 real rows migrated via
  // scripts/migrate-37-leaves.ts). See
  // docs/LEAVE-MANAGEMENT-REAL-REFERENCE.md.
  { method: 'get', path: '/api/leaves' },
  { method: 'post', path: '/api/leaves' },
  { method: 'get', path: '/api/staff-leaves-details' },
  { method: 'get', path: '/api/staff/leave-request' },
  { method: 'post', path: '/api/staff/leave-approve' },
  { method: 'get', path: '/api/staff/features-permission' },
  { method: 'get', path: '/api/staff/teachers' },
  { method: 'post', path: '/api/staff/teachers' },
  { method: 'post', path: '/api/staff/teachers/{id}/update' },
  { method: 'post', path: '/api/staff/assign-class-teacher' },
  { method: 'post', path: '/api/staff/assign-subject-teacher' },
  { method: 'get', path: '/api/staff/class-subjects' },
  { method: 'post', path: '/api/staff/unassign-class-teacher' },
  { method: 'post', path: '/api/staff/unassign-subject-teacher' },
  { method: 'get', path: '/api/staff/roles' },
  { method: 'get', path: '/api/staff/staffs' },
  { method: 'get', path: '/api/staff/departments' },
  { method: 'get', path: '/api/staff/get-roles' },
  { method: 'post', path: '/api/staff/create-student' },
  { method: 'get', path: '/api/staff/teacher-timetable' },
  { method: 'get', path: '/api/staff/student/attendance' },
  { method: 'get', path: '/api/staff/get-student-assigned-ayath/{studentId}' },
  { method: 'get', path: '/api/staff/evaluation-history/{studentId}/{targetId}' },
  { method: 'get', path: '/api/staff/class-timetable' },
  { method: 'get', path: '/api/staff/payroll-year' },
  { method: 'get', path: '/api/teacher/parent-attendance-index' },
  { method: 'get', path: '/api/teacher/student-list' },
  { method: 'get', path: '/api/teacher/get-candidates' },
  { method: 'get', path: '/api/teacher/get-student-details' },
  { method: 'post', path: '/api/teacher/submit-candidate' },
  { method: 'post', path: '/api/teacher/delete-candidate' },
  { method: 'get', path: '/api/teacher/class-list' },
  { method: 'get', path: '/api/teacher/program-items-by-class' },
  { method: 'get', path: '/api/teacher/student-multiple-class' },
  { method: 'get', path: '/api/teacher/program-item-students' },
  { method: 'get', path: '/api/staff/teacher-attendance-index' },
  { method: 'post', path: '/api/staff/teacher-attendance' },
  { method: 'post', path: '/api/teacher/submit-parent-attendance' },
  { method: 'post', path: '/api/staff/assign-student-ayath' },
  { method: 'post', path: '/api/staff/evaluation-request' },
  { method: 'get', path: '/api/teacher/pending-fee-amount' },
  { method: 'get', path: '/api/teacher/student-fees-demand' },
  { method: 'post', path: '/api/teacher/student-fees-payment' },
  { method: 'get', path: '/api/staff/school-fees-summary' },
  { method: 'get', path: '/api/staff/student-fees-by-class' },
  { method: 'get', path: '/api/staff/class-student-fees' },
  { method: 'post', path: '/api/staff/attendance' },
  { method: 'get', path: '/api/staff/attendance-list' },
  { method: 'get', path: '/api/noorani-groups/{id}/students' },
  { method: 'get', path: '/api/noorani-groups/attendance/report' },
  { method: 'post', path: '/api/noorani-groups/class/toggle' },
  // Teacher General (8) + Notifications & Announcements — Staff (7) —
  // see docs/MOBILE-API-STATUS-AUDIT.md.
  { method: 'get', path: '/api/teacher/teacher_timetable' },
  { method: 'post', path: '/api/teacher/class-detail' },
  { method: 'get', path: '/api/teacher/get-exam-list' },
  { method: 'get', path: '/api/teacher/get-lesson' },
  { method: 'get', path: '/api/teacher/subjects' },
  { method: 'get', path: '/api/teacher/sliders' },
  { method: 'get', path: '/api/teacher/file-watches/{class_section_id}/{file_id}' },
  { method: 'get', path: '/api/staff/file-view-count/{file_id}' },
  { method: 'get', path: '/api/staff/notification' },
  { method: 'post', path: '/api/staff/notification' },
  { method: 'post', path: '/api/staff/notification-delete' },
  { method: 'get', path: '/api/staff/get-announcement' },
  { method: 'post', path: '/api/staff/send-announcement' },
  { method: 'post', path: '/api/staff/update-announcement' },
  { method: 'post', path: '/api/staff/delete-announcement' },
  // PGC Registration (4 — parent-initiated counselling/complaint referral,
  // see docs/MOBILE-APP-REAL-ENDPOINTS.md "PGC REGISTRATION")
  { method: 'get', path: '/api/parent/pgc-registrations' },
  { method: 'post', path: '/api/parent/pgc-registrations' },
  { method: 'post', path: '/api/parent/pgc-registrations-update/{id}' },
  { method: 'post', path: '/api/parent/pgc-registrations-delete/{id}' },
  // Student (36)
  { method: 'post', path: '/api/student/login' },
  { method: 'get', path: '/api/student/get-profile-data' },
  { method: 'get', path: '/api/student/subjects' },
  { method: 'get', path: '/api/student/class-subjects' },
  { method: 'get', path: '/api/student/timetable' },
  { method: 'get', path: '/api/student/get-exam-list' },
  { method: 'get', path: '/api/student/student-fees-details' },
  { method: 'get', path: '/api/academic-calendar' },
  { method: 'get', path: '/api/timetable' },
  { method: 'get', path: '/api/student/get-id-card' },
  { method: 'get', path: '/api/student/school-settings' },
  { method: 'get', path: '/api/student/get-exam-details' },
  { method: 'get', path: '/api/student/lessons' },
  { method: 'get', path: '/api/student/lesson-topics' },
  { method: 'get', path: '/api/student/attendance' },
  { method: 'get', path: '/api/student/assignments' },
  { method: 'post', path: '/api/student/submit-assignment' },
  { method: 'get', path: '/api/student/announcements' },
  { method: 'get', path: '/api/student/guradian-details' },
  { method: 'post', path: '/api/student/delete-assignment-submission' },
  { method: 'get', path: '/api/student/get-student-assigned-ayath' },
  { method: 'get', path: '/api/student/exam-marks' },
  { method: 'post', path: '/api/student/forgot-password' },
  { method: 'get', path: '/api/noorani-groups/my-videos' },
  { method: 'get', path: '/api/noorani-groups/my-attendance' },
  { method: 'get', path: '/api/student/get-online-exam-list' },
  { method: 'get', path: '/api/student/get-online-exam-questions' },
  { method: 'post', path: '/api/student/submit-online-exam-answers' },
  { method: 'get', path: '/api/student/get-online-exam-result-list' },
  { method: 'get', path: '/api/student/get-online-exam-result' },
  { method: 'get', path: '/api/student/get-online-exam-report' },
  { method: 'get', path: '/api/student/get-assignments-report' },
  { method: 'get', path: '/api/student/evaluation-history/{targetId}' },
  { method: 'get', path: '/api/student/sliders' },
  { method: 'get', path: '/api/working-days' },
  { method: 'post', path: '/api/student/submit-file-watchings' },
  // Parent (36)
  { method: 'get', path: '/api/parent/subjects' },
  { method: 'post', path: '/api/parent/login' },
  { method: 'get', path: '/api/parent/get-child-profile-data' },
  { method: 'get', path: '/api/parent/lessons' },
  { method: 'get', path: '/api/parent/lesson-topics' },
  { method: 'get', path: '/api/parent/assignments' },
  { method: 'get', path: '/api/parent/school-settings' },
  { method: 'get', path: '/api/parent/attendance' },
  { method: 'get', path: '/api/parent/timetable' },
  { method: 'get', path: '/api/parent/get-exam-list' },
  { method: 'get', path: '/api/parent/exam-marks' },
  { method: 'get', path: '/api/parent/get-exam-details' },
  { method: 'get', path: '/api/parent/assignment-tasks' },
  { method: 'get', path: '/api/parent/announcements' },
  { method: 'get', path: '/api/parent/teachers' },
  { method: 'get', path: '/api/parent/fees' },
  { method: 'post', path: '/api/parent/add-fees-transaction' },
  { method: 'post', path: '/api/parent/fail-payment-transaction' },
  { method: 'post', path: '/api/parent/store-fees' },
  { method: 'get', path: '/api/parent/fees-paid-list' },
  { method: 'get', path: '/api/parent/fees-paid-receipt-pdf' },
  { method: 'post', path: '/api/student/upload-audio' },
  { method: 'get', path: '/api/parent/get-online-exam-list' },
  { method: 'get', path: '/api/parent/get-online-exam-result-list' },
  { method: 'get', path: '/api/parent/get-online-exam-result' },
  { method: 'get', path: '/api/parent/get-online-exam-report' },
  { method: 'get', path: '/api/parent/get-assignments-report' },
  { method: 'get', path: '/api/parent/fees-transactions-list' },
  { method: 'get', path: '/api/parent/sliders' },
  { method: 'post', path: '/api/parent/fees/compulsory/pay' },
  { method: 'post', path: '/api/parent/fees/optional/pay' },
  { method: 'post', path: '/api/payment-confirmation' },
  { method: 'get', path: '/api/payment-transactions' },
  { method: 'get', path: '/api/parent/fees/receipt' },
  { method: 'get', path: '/api/parent/get-affidavits' },
  { method: 'post', path: '/api/parent/submit-affidavit' },
  // Lessons (5) + Topics (4) + Assignments teacher-side (6) +
  // Announcements teacher-side (4) — see docs/MOBILE-API-STATUS-AUDIT.md.
  // Announcements teacher-side is DISTINCT from the /api/staff/
  // announcement routes above (different audience/ownership model).
  { method: 'post', path: '/api/teacher/create-lesson' },
  { method: 'post', path: '/api/teacher/update-lesson' },
  { method: 'post', path: '/api/teacher/delete-lesson' },
  { method: 'post', path: '/api/teacher/delete-file' },
  { method: 'post', path: '/api/teacher/update-file' },
  { method: 'get', path: '/api/teacher/get-topic' },
  { method: 'post', path: '/api/teacher/create-topic' },
  { method: 'post', path: '/api/teacher/update-topic' },
  { method: 'post', path: '/api/teacher/delete-topic' },
  { method: 'get', path: '/api/teacher/get-assignment-submission' },
  { method: 'post', path: '/api/teacher/update-assignment-submission' },
  { method: 'get', path: '/api/teacher/get-assignment' },
  { method: 'post', path: '/api/teacher/update-assignment' },
  { method: 'post', path: '/api/teacher/delete-assignment' },
  { method: 'post', path: '/api/teacher/create-assignment' },
  { method: 'get', path: '/api/teacher/get-announcement' },
  { method: 'post', path: '/api/teacher/send-announcement' },
  { method: 'post', path: '/api/teacher/update-announcement' },
  { method: 'post', path: '/api/teacher/delete-announcement' },
  // Attendance (teacher-marks-student, 2) + Exam Marks subject-wise (1) +
  // Offline Exam Result — staff report (1) — see
  // docs/MOBILE-API-STATUS-AUDIT.md batch 4.
  { method: 'get', path: '/api/teacher/get-attendance' },
  { method: 'post', path: '/api/teacher/submit-attendance' },
  { method: 'post', path: '/api/teacher/submit-exam-marks/subject' },
  { method: 'get', path: '/api/staff/student-offline-exam-result' },
];

const REAL_APP_ENDPOINT_KEYS = new Set(REAL_APP_ENDPOINTS.map((e) => `${e.method}:${e.path}`));

function isRealAppEndpoint(method: string, path: string): boolean {
  return REAL_APP_ENDPOINT_KEYS.has(`${method}:${path}`);
}

function resolveSchema(schema: any, components: any): any {
  if (schema?.$ref) {
    const name = schema.$ref.replace('#/components/schemas/', '');
    return components?.schemas?.[name] ?? {};
  }
  return schema ?? {};
}

/**
 * At least three real DESIGNED-marker phrasings coexist across the
 * backend's own decorators, confirmed by reading the actual generated
 * Swagger doc: "Response shape designed for this mobile API layer..."
 * and "DESIGNED — no saved example in the source collection..." (both in
 * an `@ApiResponse` description), and "DESIGNED (no saved example) —
 * ..." (in the `@ApiOperation` summary, no matching `@ApiResponse`
 * description for most of those). Checking only the response
 * description with an exact phrase (the original logic) silently mis-
 * labeled every endpoint using a different phrasing as "matched" — fixed
 * by checking both real sources for the one word every variant shares,
 * rather than chasing each exact phrase as it's invented.
 */
function isDesignedNote(summary: string | undefined, responseDescription: string | undefined): boolean {
  return /\bdesigned\b/i.test(summary ?? '') || /\bdesigned\b/i.test(responseDescription ?? '');
}

export async function fetchMobileApiEndpoints(): Promise<MobileApiEndpoint[]> {
  const res = await fetch('/api/docs-json');
  if (!res.ok) throw new Error(`Failed to load API docs (${res.status})`);
  const doc = await res.json();

  const components = doc.components ?? {};
  const endpoints: MobileApiEndpoint[] = [];

  for (const [path, pathItem] of Object.entries<any>(doc.paths ?? {})) {
    for (const method of HTTP_METHODS) {
      const op = pathItem[method];
      if (!op || !(op.tags ?? []).includes('mobile-legacy')) continue;
      if (!isRealAppEndpoint(method, path)) continue;

      const requestSchema = resolveSchema(
        op.requestBody?.content?.['application/json']?.schema,
        components,
      );
      const requestFields: RequestField[] = Object.entries<any>(requestSchema.properties ?? {}).map(
        ([name, prop]) => ({
          name,
          type: prop.type ?? (prop.$ref ? prop.$ref.replace('#/components/schemas/', '') : 'unknown'),
          required: (requestSchema.required ?? []).includes(name),
          description: prop.description ?? '',
          example: prop.example,
        }),
      );

      let successResponse: NamedExample | null = null;
      const errorResponses: NamedExample[] = [];
      for (const [status, response] of Object.entries<any>(op.responses ?? {})) {
        const schema = response.content?.['application/json']?.schema;
        const entry: NamedExample = {
          status,
          description: response.description ?? '',
          example: schema?.example ?? null,
        };
        if (status.startsWith('2')) {
          successResponse = entry;
        } else {
          errorResponses.push(entry);
        }
      }

      endpoints.push({
        method,
        path,
        summary: op.summary ?? '',
        requiresAuth: !!(op.security && op.security.length > 0),
        requestFields,
        successResponse,
        errorResponses,
        isDesigned: isDesignedNote(op.summary, successResponse?.description),
      });
    }
  }

  return endpoints;
}

/**
 * Domain grouping for the docs page, covering the real app endpoints (see
 * REAL_APP_ENDPOINTS above) — every group below was sized against that
 * exact list, with a genuine catch-all last in case a future addition to
 * REAL_APP_ENDPOINTS doesn't fit an existing bucket (surfaces as "Other"
 * rather than silently landing in the wrong group). Started at 81
 * (General/Student/Parent only); now growing to cover the Teacher/Staff
 * App surface too, see docs/TEACHER-APP-DETAILED-REFERENCE.md.
 */
export const ENDPOINT_GROUPS: { label: string; match: (e: MobileApiEndpoint) => boolean }[] = [
  {
    label: 'Login',
    match: (e) => e.path.endsWith('/login') || e.path === '/api/logout' || e.path === '/api/change-password',
  },
  {
    // Must come before 'Staff & Teacher' below — /api/staff/leave-request
    // and /api/staff/leave-approve would otherwise match that group's own
    // startsWith('/api/staff/') net. /api/leaves and
    // /api/staff-leaves-details don't match ANY other group's rule (the
    // latter doesn't start with '/api/staff/' — no slash after "staff"),
    // so without this group both would silently fall to the 'Other'
    // catch-all instead.
    label: 'Leave Management',
    match: (e) => e.path === '/api/leaves' || e.path === '/api/staff-leaves-details' || e.path.startsWith('/api/staff/leave-'),
  },
  {
    // Must come before 'Profile & Settings' below — its own
    // endsWith('/forgot-password') net would otherwise catch the bare
    // staff/teacher /api/forgot-password too (student's stays there,
    // since /api/student/forgot-password doesn't match this group).
    label: 'Staff & Teacher',
    match: (e) => e.path.startsWith('/api/staff/') || e.path.startsWith('/api/teacher/') || e.path === '/api/forgot-password',
  },
  {
    label: 'Profile & Settings',
    match: (e) =>
      e.path.includes('profile-data') ||
      e.path === '/api/update-profile' ||
      e.path.endsWith('/school-settings') ||
      e.path.endsWith('/get-id-card') ||
      e.path.endsWith('/guradian-details') ||
      e.path.endsWith('/forgot-password') ||
      e.path === '/api/get-related-student-guardian' ||
      e.path === '/api/authenticate-without-credentials' ||
      e.path === '/api/settings',
  },
  {
    label: 'Class, Subjects & Timetable',
    match: (e) =>
      e.path.endsWith('/subjects') ||
      e.path.endsWith('/class-subjects') ||
      e.path.endsWith('/timetable') ||
      e.path.endsWith('/teachers') ||
      e.path === '/api/academic-calendar' ||
      e.path === '/api/working-days' ||
      e.path === '/api/session-years' ||
      e.path === '/api/classes' ||
      e.path === '/api/medium',
  },
  {
    label: 'Attendance',
    match: (e) => e.path.endsWith('/attendance') || e.path === '/api/holidays',
  },
  {
    label: 'Assignments',
    match: (e) => e.path.includes('assignment'),
  },
  {
    label: 'Online Exams',
    match: (e) => e.path.includes('online-exam'),
  },
  {
    label: 'Exams',
    match: (e) => e.path.includes('exam'),
  },
  {
    label: 'Fees & Payments',
    match: (e) => e.path.includes('fees') || e.path.includes('payment'),
  },
  {
    label: 'Lessons',
    match: (e) => e.path.includes('lesson'),
  },
  {
    label: 'Quran & Ayath',
    match: (e) =>
      e.path.includes('ayath') ||
      e.path.includes('evaluation-history') ||
      e.path.includes('noorani') ||
      e.path.includes('upload-audio'),
  },
  {
    label: 'Communication & Media',
    match: (e) => e.path.includes('announcements') || e.path.includes('sliders') || e.path === '/api/gallery',
  },
  {
    label: 'Affidavits',
    match: (e) => e.path.includes('affidavit'),
  },
  {
    label: 'File Watch',
    match: (e) => e.path.includes('file-watching'),
  },
  {
    label: 'PGC / Counselling',
    match: (e) => e.path.includes('pgc-registrations'),
  },
  {
    label: 'Other',
    match: () => true, // genuine catch-all — should stay empty; a non-empty "Other" tab is a signal this list needs a real group added.
  },
];

export function groupEndpoints(endpoints: MobileApiEndpoint[]) {
  const groups = ENDPOINT_GROUPS.map((g) => ({ label: g.label, endpoints: [] as MobileApiEndpoint[] }));
  for (const endpoint of endpoints) {
    const groupIndex = ENDPOINT_GROUPS.findIndex((g) => g.match(endpoint));
    groups[groupIndex === -1 ? groups.length - 1 : groupIndex].endpoints.push(endpoint);
  }
  return groups.filter((g) => g.endpoints.length > 0);
}
